import { z } from "zod";
import i18n from "i18n.ts";
import {
  EnumGuestTierType,
  EnumPriceTierPriceType,
  EnumProductTierConditionClausesComparator,
  EnumProductTierConditionOperator,
  Product,
} from "gql/generated";
import {
  createMultilingual,
  formatPriceToCents,
  getMultilingualString,
  getResolvedProductSellingConfig,
} from "shared/utils";

export type TierConditionClauseFormValues = {
  questionId: string;
  comparator: EnumProductTierConditionClausesComparator;
  value: string;
};

export type TierConditionFormValues = {
  operator: EnumProductTierConditionOperator;
  clauses: TierConditionClauseFormValues[];
};

export type SellingOptionTagConfig = {
  onCheckoutCompleted?: string[];
  onRenewalSuccess?: string[];
  onRenewalPending?: string[];
  onExpiration?: string[];
  onCancellation?: string[];
  onPaymentFailed?: string[];
};

export type SellingOptionFormValues = {
  _id?: string;
  name: string;
  price: number;
  order: number;
  autoTag?: string;
  tagConfig?: SellingOptionTagConfig;
  inventoryEnabled?: boolean;
  inventoryTotalCount?: number;
  purchaseMultiple?: boolean;
  maxQuantity?: number;
  condition?: TierConditionFormValues;
};

export type SellingFieldsFormValues = {
  price?: number;
  sellingOptions: SellingOptionFormValues[];
  inventoryEnabled?: boolean;
  inventoryTotalCount?: number;
  purchaseMultiple?: boolean;
  maxQuantity?: number;
  dynamicFeeSurvey?: string;
};

type ProductWithSellingConfig = Product & {
  sellingConfig?: {
    inventory?: {
      currentCount?: number | null;
      totalCount?: number | null;
    } | null;
  } | null;
};

export const createSellingFieldsSchema = () =>
  z.object({
    price: z.preprocess(
      value => {
        if (value === "" || value === null || value === undefined) {
          return undefined;
        }
        if (typeof value === "string") {
          return Number(value);
        }
        return value;
      },
      z.union([z.number(), z.undefined()]),
    ),
    sellingOptions: z
      .array(
        z.object({
          _id: z.string().optional(),
          name: z.string().trim().min(1),
          price: z.coerce.number().min(0),
          order: z.coerce.number(),
          autoTag: z.string().optional(),
          inventoryEnabled: z.boolean().optional(),
          inventoryTotalCount: z
            .preprocess(
              value => (typeof value === "string" ? Number(value) : value),
              z.number().min(0),
            )
            .optional(),
          purchaseMultiple: z.boolean().optional(),
          maxQuantity: z
            .preprocess(value => {
              if (value === "" || value === null || value === undefined) {
                return undefined;
              }
              if (typeof value === "string") {
                return Number(value);
              }
              return value;
            }, z.number().optional())
            .optional(),
          condition: z
            .object({
              operator: z.nativeEnum(EnumProductTierConditionOperator),
              clauses: z.array(
                z.object({
                  questionId: z.string(),
                  comparator: z.nativeEnum(
                    EnumProductTierConditionClausesComparator,
                  ),
                  value: z.string(),
                }),
              ),
            })
            .optional(),
        }),
      )
      .optional()
      .default([]),
    inventoryEnabled: z.boolean().optional(),
    inventoryTotalCount: z
      .preprocess(
        value => (typeof value === "string" ? Number(value) : value),
        z.number().min(0),
      )
      .optional(),
    purchaseMultiple: z.boolean().optional(),
    maxQuantity: z
      .preprocess(value => {
        if (value === "" || value === null || value === undefined) {
          return undefined;
        }
        if (typeof value === "string") {
          return Number(value);
        }
        return value;
      }, z.number().optional())
      .optional(),
    dynamicFeeSurvey: z.string().optional(),
  });

export const validateSellingFields = (
  data: SellingFieldsFormValues,
  ctx: z.RefinementCtx,
  translationKeyPrefix: string,
) => {
  if (data.purchaseMultiple && data.maxQuantity && data.maxQuantity < 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["maxQuantity"],
      message: i18n.t(`${translationKeyPrefix}.ERRORS.MAX_QUANTITY`),
    });
  }
};

export const getSellingFieldsDefaultValues = (
  product: ProductWithSellingConfig | null,
): SellingFieldsFormValues => {
  const sellingConfig = getResolvedProductSellingConfig(product);

  return {
    price:
      sellingConfig.basePrice > 0
        ? sellingConfig.basePrice / 100
        : ("" as unknown as number),
    sellingOptions: sellingConfig.options.map(option => ({
      _id: option._id,
      name: getMultilingualString(option.name ?? {}),
      price: option.price,
      order: option.order,
      autoTag: option.autoTag ?? undefined,
      tagConfig: option.tagConfig
        ? {
            onCheckoutCompleted:
              (option.tagConfig as any).onCheckoutCompleted?.map(String) ?? [],
            onRenewalSuccess:
              (option.tagConfig as any).onRenewalSuccess?.map(String) ?? [],
            onRenewalPending:
              (option.tagConfig as any).onRenewalPending?.map(String) ?? [],
            onExpiration:
              (option.tagConfig as any).onExpiration?.map(String) ?? [],
            onCancellation:
              (option.tagConfig as any).onCancellation?.map(String) ?? [],
            onPaymentFailed:
              (option.tagConfig as any).onPaymentFailed?.map(String) ?? [],
          }
        : undefined,
      inventoryEnabled: option.inventory?.enabled ?? false,
      inventoryTotalCount: option.inventory?.totalCount ?? 0,
      purchaseMultiple: option.quantity?.enabled ?? false,
      maxQuantity: option.quantity?.maxQuantity ?? undefined,
      condition: option.condition
        ? {
            operator:
              (option.condition.operator as EnumProductTierConditionOperator) ??
              EnumProductTierConditionOperator.And,
            clauses: (option.condition.clauses ?? [])
              .filter((c): c is NonNullable<typeof c> => c != null)
              .map(c => ({
                questionId: c.questionId,
                comparator: c.comparator,
                value: c.value,
              })),
          }
        : undefined,
    })),
    inventoryEnabled: sellingConfig.inventory?.enabled ?? false,
    inventoryTotalCount: sellingConfig.inventory?.totalCount ?? 0,
    purchaseMultiple: sellingConfig.quantity?.enabled ?? false,
    maxQuantity: sellingConfig.quantity?.maxQuantity ?? undefined,
    dynamicFeeSurvey: product?.sellingConfig?.dynamicFeeSurvey ?? undefined,
  };
};

type BuildSellingRecordArgs = {
  values: SellingFieldsFormValues;
  product: ProductWithSellingConfig | null;
  recurringBillingIntervalMonths?: number;
  recurringRenewalType?: string;
  recurringFixedRenewalMonth?: number;
  recurringFixedRenewalDay?: number;
  recurringProRateEnabled?: boolean;
  recurringMembershipLinked?: boolean;
};

export const buildSellingRecord = ({
  values,
  product,
  recurringBillingIntervalMonths,
  recurringRenewalType,
  recurringFixedRenewalMonth,
  recurringFixedRenewalDay,
  recurringProRateEnabled,
  recurringMembershipLinked,
}: BuildSellingRecordArgs) => {
  const hasOptions = values.sellingOptions.length > 0;
  const basePrice = !hasOptions ? formatPriceToCents(values.price ?? 0) : 0;

  const optionsInput = hasOptions
    ? values.sellingOptions.map(rawOption => {
        const option = rawOption.condition
          ? {
              ...rawOption,
              condition: {
                ...rawOption.condition,
                clauses: rawOption.condition.clauses.filter(
                  c => c.questionId.length > 0,
                ),
              },
            }
          : rawOption;
        const existingOption =
          product?.sellingConfig?.options?.find(
            item => item?._id && option._id && String(item._id) === option._id,
          ) ??
          product?.productTiers?.find(
            item => item?._id && option._id && String(item._id) === option._id,
          );
        const nextTotal = option.inventoryTotalCount ?? 0;
        const existingInventory = existingOption?.inventory;
        const preserveRemaining =
          existingInventory?.enabled &&
          existingInventory.totalCount === nextTotal;

        return {
          ...(option._id ? { _id: option._id } : {}),
          name: createMultilingual(option.name),
          price: option.price,
          order: option.order,
          autoTag: option.autoTag || undefined,
          tagConfig:
            option.tagConfig &&
            Object.values(option.tagConfig).some(v => v?.length)
              ? option.tagConfig
              : undefined,
          inventory: option.inventoryEnabled
            ? {
                enabled: true,
                totalCount: nextTotal,
                currentCount: preserveRemaining
                  ? (existingInventory?.currentCount ?? nextTotal)
                  : nextTotal,
              }
            : { enabled: false, totalCount: 0, currentCount: 0 },
          quantity: {
            enabled: option.purchaseMultiple ?? false,
            maxQuantity:
              option.purchaseMultiple && option.maxQuantity
                ? Number(option.maxQuantity)
                : undefined,
          },
          ...(option.condition &&
          option.condition.clauses.length > 0 &&
          values.dynamicFeeSurvey
            ? {
                condition: {
                  operator: option.condition.operator,
                  clauses: option.condition.clauses.map(c => ({
                    questionId: c.questionId,
                    comparator: c.comparator,
                    value: c.value,
                  })),
                },
              }
            : {}),
        };
      })
    : [];

  const existingInventory =
    product?.sellingConfig?.inventory ?? product?.inventorySettings;

  const inventoryInput = hasOptions
    ? {
        enabled: false,
        totalCount: existingInventory?.totalCount ?? 0,
        currentCount: existingInventory?.currentCount ?? 0,
      }
    : values.inventoryEnabled
      ? {
          enabled: true,
          totalCount: values.inventoryTotalCount ?? 0,
          currentCount: values.inventoryTotalCount ?? 0,
        }
      : {
          enabled: false,
          totalCount:
            values.inventoryTotalCount ?? existingInventory?.totalCount ?? 0,
          currentCount: existingInventory?.currentCount ?? 0,
        };

  const quantityInput = {
    enabled: values.purchaseMultiple ?? false,
    maxQuantity:
      values.purchaseMultiple && values.maxQuantity
        ? Number(values.maxQuantity)
        : undefined,
  };

  const typedRenewalType = recurringRenewalType as any;

  const recurringInput =
    recurringBillingIntervalMonths != null
      ? {
          enabled: true,
          billingIntervalMonths: recurringBillingIntervalMonths,
          renewalType: typedRenewalType,
          fixedRenewalMonth: recurringFixedRenewalMonth,
          fixedRenewalDay: recurringFixedRenewalDay,
          proRateEnabled: recurringProRateEnabled,
          membershipLinked: recurringMembershipLinked ?? false,
        }
      : {
          enabled: false,
          billingIntervalMonths: undefined,
        };

  return {
    sellingConfig: {
      mode: hasOptions ? "CHOICE" : "SINGLE",
      basePrice,
      options: optionsInput,
      inventory: inventoryInput,
      quantity: quantityInput,
      recurring: recurringInput,
      dynamicFeeSurvey: values.dynamicFeeSurvey || undefined,
    },
    pricing: {
      tiers: [
        {
          price: basePrice,
          type: EnumGuestTierType.General,
          priceType: EnumPriceTierPriceType.Flat,
        },
      ],
    },
    productTiers: optionsInput,
    inventorySettings: inventoryInput,
    quantitySettings: quantityInput,
    ...(recurringBillingIntervalMonths != null
      ? {
          renewableFeeMetadata: {
            billingIntervalMonths: recurringBillingIntervalMonths,
            renewalType: typedRenewalType,
            fixedRenewalMonth: recurringFixedRenewalMonth,
            fixedRenewalDay: recurringFixedRenewalDay,
            proRateEnabled: recurringProRateEnabled,
            membershipLinked: recurringMembershipLinked ?? false,
          },
        }
      : {}),
  };
};
